<script setup lang="ts">
import type { MediaSummary, PlaybackRecord } from '../../shared/contracts'
import { ArrowRight, Bookmark, History, Search, Trash2, X } from '@lucide/vue'
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import MediaGrid from '@/components/media/MediaGrid.vue'
import ConfirmDialog from '@/components/ui/ConfirmDialog.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import { useHorizontalSwipe } from '@/composables/useHorizontalSwipe'
import { useOverlayHistory } from '@/composables/useOverlayHistory'
import { useToast } from '@/composables/useToast'
import { useLibraryStore } from '@/stores/library'
import { matchesLibrarySearch } from '@/utils/librarySearch'

defineOptions({ name: 'LibraryView' })

const route = useRoute()
const router = useRouter()
const library = useLibraryStore()
const toast = useToast()
type LibraryTab = 'favorites' | 'history'
const tab = ref<LibraryTab>(route.query.tab === 'history' ? 'history' : 'favorites')
const clearOpen = ref(false)
const deleteOpen = ref(false)
const historyQuery = ref(route.query.tab === 'history' && typeof route.query.q === 'string' ? route.query.q.slice(0, 100) : '')
const pendingDelete = ref<PlaybackRecord>()
const { open: openClear, close: closeClear } = useOverlayHistory(clearOpen, 'clear-history')
const { open: openDelete, close: closeDelete } = useOverlayHistory(deleteOpen, 'delete-history-entry')
const searchingHistory = computed(() => Boolean(historyQuery.value.trim()))
const visibleHistory = computed(() => searchingHistory.value
    ? library.history.filter(record => matchesLibrarySearch(record.media, historyQuery.value))
    : library.history)
const items = computed(() => tab.value === 'favorites' ? library.favorites.map(item => item.media) : visibleHistory.value.map(item => item.media))
const libraryPath = computed(() => router.resolve({
    path: '/library',
    query: {
        tab: tab.value,
        ...(tab.value === 'history' && historyQuery.value.trim() ? { q: historyQuery.value.trim() } : {}),
    },
}).fullPath)

function switchTab(value: LibraryTab) {
    if (tab.value === value) { return }
    tab.value = value
    router.replace({ query: { tab: value, ...(value === 'history' && historyQuery.value.trim() ? { q: historyQuery.value.trim() } : {}) } })
}

const tabSwipe = useHorizontalSwipe(direction => switchTab(direction > 0 ? 'history' : 'favorites'))

watch(() => [route.query.tab, route.query.q] as const, ([value, query]) => {
    tab.value = value === 'history' ? 'history' : 'favorites'
    if (tab.value === 'history') { historyQuery.value = typeof query === 'string' ? query.slice(0, 100) : '' }
})

function clearHistory() {
    library.clearHistory()
    clearHistorySearch()
    closeClear()
    toast.show('观看历史已清空', 'success')
}

function clearHistorySearch() {
    historyQuery.value = ''
    if (tab.value !== 'history' || !route.query.q) { return }
    const query = { ...route.query }
    delete query.q
    router.replace({ query })
}

function confirmRemove(media: MediaSummary) {
    pendingDelete.value = library.getRecord(media)
    if (pendingDelete.value) { openDelete() }
}

function removeHistory() {
    if (!pendingDelete.value) { return }
    library.removeHistory(pendingDelete.value.key)
    closeDelete()
    toast.show('已从观看历史中移除', 'success')
}
</script>

<template>
    <div class="page-shell">
        <header class="border-b border-[var(--c-border)] pb-8">
            <div>
                <p class="mb-3 text-[10px] text-[var(--accent)] font-700 tracking-[.14em] uppercase">
                    留下喜欢的，也记住看过的
                </p>
                <h1 class="page-title">
                    收藏与历史
                </h1>
            </div>
        </header>

        <div class="flex gap-8 border-b border-[var(--c-border)] py-5">
            <button class="flex items-center justify-center gap-2 rounded-sm text-sm font-600 transition focus-ring" :class="tab === 'favorites' ? 'text-[var(--c-text)]' : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)]'" @click="switchTab('favorites')">
                <Bookmark class="size-4" />我的收藏 <span class="text-[var(--c-text-faint)]">{{ library.favorites.length }}</span>
            </button>
            <button class="flex items-center justify-center gap-2 rounded-sm text-sm font-600 transition focus-ring" :class="tab === 'history' ? 'text-[var(--c-text)]' : 'text-[var(--c-text-muted)] hover:text-[var(--c-text)]'" @click="switchTab('history')">
                <History class="size-4" />观看历史 <span class="text-[var(--c-text-faint)]">{{ library.history.length }}</span>
            </button>
        </div>

        <div v-if="tab === 'history' && library.history.length" class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div class="relative w-full sm:max-w-md">
                <Search class="pointer-events-none absolute left-3.5 top-1/2 size-4 text-[var(--c-text-muted)] -translate-y-1/2" />
                <input v-model="historyQuery" class="history-search h-11 w-full border border-[var(--c-border)] rounded-lg bg-[var(--c-surface-raised)] pl-10 pr-11 text-sm text-[var(--c-text)] outline-none transition focus:border-[var(--c-border-strong)] placeholder:text-[var(--c-text-faint)] focus-ring" type="search" autocomplete="off" enterkeyhint="search" maxlength="100" :spellcheck="false" placeholder="搜索片名、年份或来源" aria-label="搜索观看历史">
                <button v-if="historyQuery" class="absolute right-1.5 top-1/2 grid size-8 place-items-center rounded-md text-[var(--c-text-muted)] transition -translate-y-1/2 hover:bg-[var(--c-fill-hover)] hover:text-[var(--c-text)] focus-ring" aria-label="清除搜索" @click="clearHistorySearch">
                    <X class="size-4" />
                </button>
            </div>
            <button class="min-h-10 inline-flex shrink-0 items-center self-end justify-center gap-2 rounded-full px-3 text-sm text-[var(--c-text-muted)] transition sm:self-auto hover:text-[var(--c-danger)] focus-ring" @click="openClear">
                <Trash2 class="size-4" /><span>清空历史</span>
            </button>
        </div>

        <section class="library-swipe-pane" :class="tab === 'history' && library.history.length ? 'mt-6' : 'mt-10'" @touchstart="tabSwipe.onTouchStart" @touchend="tabSwipe.onTouchEnd" @touchcancel="tabSwipe.onTouchCancel">
            <MediaGrid v-if="items.length" :items="items" :records="library.history" :removable="tab === 'history'" :from="libraryPath" :show-playback-status="tab === 'history'" direct-playback @remove="confirmRemove" />
            <EmptyState v-else :title="tab === 'favorites' ? '还没有收藏' : searchingHistory ? '没有找到相关影片' : '还没有观看历史'" :description="tab === 'favorites' ? '遇到喜欢的影片时点一下收藏，就能在这里找到。' : searchingHistory ? '试试其他片名、年份或来源。' : '看过的内容和播放位置会自动留在这里。'">
                <button v-if="tab === 'history' && searchingHistory" class="mt-5 btn-ghost" @click="clearHistorySearch">
                    清除搜索 <X class="size-4" />
                </button>
                <RouterLink v-else to="/" class="mt-5 btn-ghost">
                    去首页看看 <ArrowRight class="size-4" />
                </RouterLink>
            </EmptyState>
        </section>

        <ConfirmDialog
            :open="deleteOpen"
            :title="`从观看历史中移除《${pendingDelete?.media.title || ''}》`"
            description="移除后会清除这部影片的观看位置，但不会影响收藏。"
            eyebrow="移除历史"
            confirm-label="确认移除"
            @close="closeDelete"
            @confirm="removeHistory"
            @after-leave="pendingDelete = undefined"
        />

        <ConfirmDialog
            :open="clearOpen"
            title="清空全部观看历史"
            description="所有影片的观看位置和历史数据都会从当前设备移除，此操作无法撤销。"
            eyebrow="清空历史"
            confirm-label="确认清空"
            @close="closeClear"
            @confirm="clearHistory"
        />
    </div>
</template>

<style scoped>
.library-swipe-pane {
    min-height: max(18rem, 55dvh);
    touch-action: pan-y;
    overscroll-behavior-x: none;
    user-select: none;
}

.history-search {
    appearance: none;
    -webkit-appearance: none;
}

.history-search::-webkit-search-cancel-button {
    display: none;
    appearance: none;
    -webkit-appearance: none;
}
</style>
